import { useEffect, useRef, useState } from 'react';
import Overlay from '../../ui/Overlay';
import Button from '../../ui/Button';
import { createBounty, getBounty, requestFunding, startBounty, updateBounty } from '../../apis/bounties/bounties';
import Feedbackclose from '../../assets/icons/Feedbackclose';
import ModalBox from '../../ui/ModalBox';
import { sanitizeBountyData } from '../../helpers/sanitizeBountyDetails';
import { useLoadingState } from '../../hooks/useLoader';
import Heading from '../../ui/Heading';
import TextWithTooltip from '../../ui/TextWithTooltip';
import DatePicker from 'react-datepicker';
import Input from '../../ui/Input';
import { useStepFourInputs } from './bountiesHooks/useStepFourInputs';
import { useController, useForm } from 'react-hook-form';

import Loader from '../../ui/Loader';
import { extractTimeComponents } from '../../helpers/dataModifier';
import { dateToIsoString } from '../../helpers/dateToIsoString';
import { convertToMilliseconds } from '../../helpers/addTime';
import { toast } from 'sonner';
import RadioButtonComponent from '../../ui/RadioButtonComponent';
import { fundBounty } from '../../blockchainInteraction/finaliseGrading';
import Dropdown from '../../ui/Dropdown';
import { createPortal } from 'react-dom';
import ButtonNew from '../../ui/ButtonNew';
function StartBountyModal({
    close,
    bountyName,
    bountyId,
    handleConfirmAction,
    isComingFromGreenpillEvent,
    treasuryWallets,
}) {
    const modalRef = useRef(null); // Reference for the modal content
    const { loading, startLoading, stopLoading } = useLoadingState();
    const [fundingMode, setFundingMode] = useState('We will self-fund the bounty');
    const {
        loading: gettingBounty,
        startLoading: startGettingBounty,
        stopLoading: stopGettingBounty,
    } = useLoadingState();
    const [bountyPayload, setBountyPayload] = useState({});
    const isFundingByLandscape = fundingMode === 'Extend funding date, via Impact Landscapes';
    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            ...bountyPayload,

            ...(isFundingByLandscape
                ? {
                    fundingRequiredBy: bountyPayload?.fundingRequiredBy
                        ? bountyPayload?.fundingRequiredBy
                        : undefined,
                }
                : {
                    expirationTime: bountyPayload?.estimatedCompletionTime
                        ? bountyPayload?.estimatedCompletionTime
                        : undefined,
                    day: bountyPayload?.estimatedCompletionTime
                        ? extractTimeComponents(bountyPayload?.estimatedCompletionTime)?.days
                        : 0,
                    hours: bountyPayload?.estimatedCompletionTime
                        ? extractTimeComponents(bountyPayload?.estimatedCompletionTime)?.hours
                        : 0,
                    minutes: bountyPayload?.estimatedCompletionTime
                        ? extractTimeComponents(bountyPayload?.estimatedCompletionTime)?.minutes
                        : 0,
                }),
        },
    });
    const { expirationTimeField, dayField, hoursField, minutesField } = useStepFourInputs(
        control,
        !isFundingByLandscape
    );
    const { field: validationTillDateField } = useController({
        name: 'validationTillDate',
        control,
        rules: {
            required: isFundingByLandscape ? false : 'Please enter a date',

            // validate: (value) => {
            //     if (!withRewardTokenField?.value) return true;
            //     if (!expirationTimeField.value) {
            //         return 'Please select the first date before selecting the second date.';
            //     }

            //     return true;
            // },
        },
    });

    const { field: fundingRequiredByField } = useController({
        name: 'fundingRequiredBy',
        control,
        rules: {
            required:
                bountyPayload?.status === 'Funding Failed'
                    ? isFundingByLandscape
                        ? false
                        : 'Please enter a date'
                    : false,

            // validate: (value) => {
            //     if (!withRewardTokenField?.value) return true;
            //     if (!expirationTimeField.value) {
            //         return 'Please select the first date before selecting the second date.';
            //     }

            //     return true;
            // },
        },
    });

    const hasError = errors?.day?.message || errors?.hours?.message || errors?.minutes?.message ? true : false;

    useEffect(() => {
        // Fetch the bounty data asynchronously
        const fetchData = async () => {
            try {
                startGettingBounty();
                const data = await getBounty(bountyId);
                setBountyPayload(data);
                // Extract time components and update form values
                const timeComponents = extractTimeComponents(data?.estimatedCompletionTime || 0);
                reset({
                    ...data,
                    expirationTime: data?.expirationTimestamp ? new Date(data?.expirationTimestamp) : undefined,
                    day: timeComponents?.days || 0,
                    hours: timeComponents?.hours || 0,
                    minutes: timeComponents?.minutes || 0,
                    validationTillDate: data?.validationTillDate ? new Date(data?.validationTillDate) : undefined,
                    fundingRequiredBy: data?.fundingRequiredBy ? new Date(data?.fundingRequiredBy) : undefined,
                });
            } catch (error) {
                console.error('Error fetching bounty:', error);
            } finally {
                stopGettingBounty();
            }
        };
        fetchData();

        // Handle click outside the modal
        function handleClickOutside(e) {
            // Check if the click was outside the modal content
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                close(); // Close the modal if clicked outside
            }
        }

        // Add event listener in the capture phase to ensure proper handling
        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            // Clean up the event listener on unmount
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [close, bountyId]);

    const onSubmit = async (data) => {
        if (data?.status === 'Funding Failed' && isFundingByLandscape) {
            const payload = {
                ...bountyPayload,
                fundingRequiredBy: dateToIsoString(new Date(data?.fundingRequiredBy)),
            };
            const {
                __v,
                id,
                _id,
                createdAt,
                updatedAt,
                creatorUid,
                expirationTimestamp,
                estimatedCompletionTime,
                validationTillDate,
                ...payload3
            } = payload;

            try {
                const { success, status } = await requestFunding(
                    {},
                    bountyId,
                    new Date(data?.fundingRequiredBy)?.toISOString()
                );

                if (status === 400) {
                    toast?.error('Please select a time that is at least 10 minutes in the future.');
                }
                if (success) {
                    toast.success('Funding Date Extended Successfully');
                    close();

                    return;
                }
            } catch (error) {
                console.log(error);
            }
        } else {
            const { expirationTime, day, hours, minutes, validationTillDate } = data;
            const expirationDate = new Date(expirationTime);
            const validationDate = new Date(validationTillDate);
            const currentDate = new Date();
            // Validation: expirationTime should be in the future
            if (expirationDate <= currentDate) {
                toast.error('Expiration time must be in the future.');
                return;
            }

            // Validation: validationTillDate should be after expirationTime
            if (validationDate <= expirationDate) {
                toast.error('Validation till date must be after the expiration time.');
                return;
            }

            const payload = {
                ...bountyPayload,
                expirationTimestamp: dateToIsoString(new Date(expirationTime)),
                estimatedCompletionTime: convertToMilliseconds(Number(day), Number(hours), Number(minutes)),
                validationTillDate: dateToIsoString(new Date(validationTillDate)),
            };

            const {
                __v,
                id,
                _id,
                createdAt,
                updatedAt,
                creatorUid,
                solanaTreasuryWalletAddress,
                treasuryWalletAddress,
                ...payload3
            } = payload;
            // solanaTreasuryWalletAddress ,treasuryWalletAddress
            const sanitizedPayload = sanitizeBountyData(payload3);

            try {
                startLoading();

                const response = await updateBounty(sanitizedPayload, bountyPayload?._id);
                if (!response) {
                    stopLoading();
                    toast.error('Unable to create bounty. Please check with Atlantis customer support. ');
                    return;
                }
                if (data?.status === 'Funding Failed') {
                    const { success, transactionHash, code } = await fundBounty({
                        tokenAddress: bountyPayload?.tokenInfo?.address,
                        receiver: treasuryWallets?.walletAddress,
                        amount: bountyPayload?.rewardToken?.totalRewardAmount - bountyPayload?.fundedAmount,
                        decimals: bountyPayload?.tokenInfo?.decimals,
                        chainId: bountyPayload?.tokenInfo?.chainId,
                    });
                    if (success) {
                        const res2 = await startBounty({}, bountyId, transactionHash);

                        if (res2?.id) {
                            // setStep(7);
                            toast.success('Bounty is now live on the ‘Impact Miner’ App!');
                            handleConfirmAction(res2);
                        }
                    } else {
                        toast.error(code);
                    }
                } else {
                    if (response?._id) {
                        const creationPayload = {
                            organisationId: id,
                            requestType: 'BOUNTY_CREATION',
                            bountyId: response._id,
                            lastUpdateTime: response.updatedAt,
                        };

                        if (response?.status === 'Draft' || response?.status === 'Deploy Ready') {
                            const responseRequest = await startBounty(creationPayload, creationPayload?.bountyId);

                            if (responseRequest?.id) {
                                // setStep(7);
                                toast.success('Bounty is now live on the ‘Impact Miner’ App!');
                                handleConfirmAction(responseRequest);
                            }
                        }
                    }
                }
            } catch (error) {
                console.log(error);
            } finally {
                stopLoading();
                close();
            }
        }
    };

    return createPortal(
        <Overlay>
            <div
                ref={modalRef} // Attach ref to modal content
                className="absolute rounded-lg left-[50%]     bg-primaryBg translate-x-[-50%] translate-y-[-50%] top-[50%]"
                onClick={(e) => e.stopPropagation()} // Stop propagation to avoid closing the modal when clicked inside
            >
                <ModalBox
                    close={close}
                    width="w-[531px]"
                    isConfirmModal={false}
                    onSubmit={onSubmit}
                    primaryBtnText={'Publish'}
                    isTypeSubmit={true}
                    loading={loading}
                    secondaryBtnText={'Cancel'}
                >
                    {gettingBounty ? (
                        <div className="min-h-[400px] flex items-center justify-center ">
                            <Loader />
                        </div>
                    ) : !['Funding Failed', 'Draft', 'Deploy Ready'].includes(bountyPayload?.status) ? (
                        <div className="p-4">
                            <p className="">
                                You can't publish the bounty or extend the funding date because its current status is{' '}
                                <strong>{bountyPayload?.status}</strong>.
                            </p>
                        </div>
                    ) : (
                        <div>
                            <div className="pb-4 border-b border-borderUnderline">
                                {bountyPayload?.status === 'Funding Failed' ? (
                                    <div>
                                        <h3 className="text-xl font-bold text-textPrimary">
                                            Funding failed, how do you want to proceed?
                                        </h3>
                                        <p className="mt-2 text-textSecondary">
                                            The bounty has received{' '}
                                            {(
                                                (Number(bountyPayload?.fundedAmount) /
                                                    Number(bountyPayload?.rewardToken?.totalRewardAmount)) *
                                                100
                                            )?.toFixed(2)}
                                            {'% '}
                                            of the funds (${Number(bountyPayload?.fundedAmount)?.toFixed(2)}/
                                            {Number(bountyPayload?.rewardToken?.totalRewardAmount)?.toFixed()}) so if
                                            you choose to self fund you will only have to pay for the rest of the amount
                                        </p>
                                    </div>
                                ) : (
                                    <h3 className="text-xl font-bold text-textPrimary"> Publish Bounty</h3>
                                )}
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} className="pt-4">
                                {bountyPayload?.status === 'Funding Failed' && (
                                    <div className="mb-5">
                                        <RadioButtonComponent
                                            className={'flex-col mt-2'}
                                            isTooltip={true}
                                            selectedOption={fundingMode}
                                            render={(option) => {
                                                return option === 'We will self-fund the bounty' ? (
                                                    <span className="capitalize ml-2 ">{option}</span>
                                                ) : (
                                                    <TextWithTooltip
                                                        heading={<span className="capitalize ml-2  ">{option}</span>}
                                                    >
                                                        <p className="text-sm">
                                                            Impact Landscapes is a platform designed for bounty backers
                                                            who want to fund bounties. Extending the funding date will
                                                            allow for more time to find funding via Impact Landscapes.
                                                        </p>
                                                    </TextWithTooltip>
                                                );
                                            }}
                                            onSelectOption={(value) => {
                                                setFundingMode(value);
                                                if (value === 'We will self-fund the bounty') {
                                                    // backerHandler(true);
                                                    // validationHandler(true);
                                                }
                                                if (value === 'Extend funding date, via Impact Landscapes') {
                                                    // backerHandler(false);
                                                    // validationHandler(false);
                                                }
                                            }}
                                            options={[
                                                'We will self-fund the bounty',
                                                'Extend funding date, via Impact Landscapes',
                                            ]}
                                        />
                                    </div>
                                )}

                                {isFundingByLandscape ? (
                                    <div className="mb-5">
                                        <div className="flex gap-3 mb-1">
                                            <TextWithTooltip
                                                expandedTextWidth={'400px'}
                                                heading={
                                                    <Heading type="xsmall" className="capitalize  text-textPrimary">
                                                        When do you need the funding by?
                                                        <sup className="text-lg relative top-[1px]">*</sup>
                                                    </Heading>
                                                }
                                            >
                                                <p className="text-sm">
                                                    If the bounty doesn't receive the required funding amount by this
                                                    date it will be under the status 'funding failed' after which you
                                                    can self fund the bounty or delete it
                                                </p>
                                            </TextWithTooltip>
                                        </div>

                                        <div>
                                            <DatePicker
                                                showTimeSelect
                                                minDate={new Date()}
                                                className="border border-borderUnderline w-[230px] shadow-sm text-sm bg-grayBgNormal placeholder:text-textTertiary font-semibold rounded-md px-4 py-2 focus:outline-none focus:border-blueNormal focus:ring-0"
                                                // minTime={new Date()}
                                                // maxTime={new Date(0, 0, 0, 19, 0)}
                                                placeholderText="Set Date & Time"
                                                selected={fundingRequiredByField.value}
                                                onChange={(date) => fundingRequiredByField.onChange(date)}
                                                dateFormat="MMMM d, yyyy h:mmaa"
                                                timeIntervals={1}

                                            // customInput={<CustomDateInput placeholder="Set Date & Time" className="bg-grayBgNormal" />}
                                            />
                                            {errors.fundingRequiredBy && (
                                                <p className="text-foundryRed">{errors.fundingRequiredBy.message}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-5">
                                            <div className="flex gap-3 mb-1">
                                                <TextWithTooltip
                                                    expandedTextWidth={'400px'}
                                                    heading={
                                                        <Heading
                                                            type="xsmall"
                                                            className="capitalize  text-textPrimary"
                                                        >
                                                            Once published, how long should the bounty be live?{' '}
                                                            <sup className="text-lg relative top-[1px]">*</sup>
                                                        </Heading>
                                                    }
                                                >
                                                    <p className="text-sm">
                                                        This is the bounty expiration time after which it will disappear
                                                        from Impact Miner and users will not be able to participate
                                                    </p>
                                                </TextWithTooltip>
                                            </div>

                                            <div>
                                                <DatePicker
                                                    showTimeSelect
                                                    minDate={new Date()}
                                                    className="border border-borderUnderline w-[230px] shadow-sm text-sm bg-grayBgNormal placeholder:text-textTertiary font-semibold rounded-md px-4 py-2 focus:outline-none focus:border-blueNormal focus:ring-0"
                                                    // minTime={new Date()}
                                                    // maxTime={new Date(0, 0, 0, 19, 0)}
                                                    placeholderText="Set Date & Time"
                                                    selected={expirationTimeField.value}
                                                    onChange={(date) => expirationTimeField.onChange(date)}
                                                    dateFormat="MMMM d, yyyy h:mmaa"
                                                    timeIntervals={1}

                                                // customInput={<CustomDateInput placeholder="Set Date & Time" className="bg-grayBgNormal" />}
                                                />
                                                {errors.expirationTime && (
                                                    <p className="text-foundryRed">{errors.expirationTime.message}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="my-5">
                                            <div className="flex gap-3 mb-1">
                                                <TextWithTooltip
                                                    heading={
                                                        <Heading
                                                            type="xsmall"
                                                            className="capitalize  text-textPrimary"
                                                        >
                                                            Estimated time to complete bounty{' '}
                                                            <sup className="text-lg relative top-[1px]">*</sup>
                                                        </Heading>
                                                    }
                                                >
                                                    <p className="text-sm">
                                                        An estimation of time will help users on Impact Miner understand
                                                        how long it will take to complete the bounty
                                                    </p>
                                                </TextWithTooltip>
                                            </div>
                                            <div className="flex w-[50%] mt-1 gap-1">
                                                <div>
                                                    <div>
                                                        <Input
                                                            {...dayField}
                                                            className="w-full text-sm font-semibold"
                                                            type="number"
                                                            placeholder={0}
                                                        />
                                                        <p className="">day</p>
                                                    </div>
                                                </div>

                                                <div>
                                                    <div>
                                                        <Input
                                                            {...hoursField}
                                                            className="w-full text-sm font-semibold"
                                                            type="number"
                                                            placeholder={0}
                                                        />
                                                        <p>hours</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div>
                                                        <Input
                                                            {...minutesField}
                                                            className="w-full text-sm font-semibold"
                                                            type="number"
                                                            placeholder={0}
                                                        />
                                                        <p>minutes</p>
                                                    </div>
                                                </div>
                                            </div>
                                            {hasError && (
                                                <p className="text-foundryRed">{'Please enter the estimated time'}</p>
                                            )}
                                        </div>
                                        <div className="my-5">
                                            <div className="flex gap-3 mb-1">
                                                <TextWithTooltip
                                                    heading={
                                                        <Heading
                                                            type="xsmall"
                                                            className="capitalize  text-textPrimary"
                                                        >
                                                            Set Deadline for validating all submissions{' '}
                                                            <sup className="text-lg relative top-[1px]">*</sup>
                                                        </Heading>
                                                    }
                                                >
                                                    <p className="text-sm">
                                                        Validation determines the score of each submission and in turn
                                                        the winners.
                                                    </p>
                                                    <p className="text-sm mt-3">
                                                        Setting a deadline will inform users how long it will take for
                                                        the results to be announced
                                                    </p>
                                                </TextWithTooltip>
                                            </div>
                                            <div>
                                                <DatePicker
                                                    minDate={new Date(expirationTimeField.value)}
                                                    showTimeSelect
                                                    className="border border-borderUnderline w-[230px] shadow-sm text-sm bg-grayBgNormal placeholder:text-textTertiary font-semibold rounded-md px-4 py-2 focus:outline-none focus:border-blueNormal focus:ring-0"
                                                    placeholderText="Set Date & Time"
                                                    selected={validationTillDateField?.value}
                                                    disabled={!expirationTimeField?.value}
                                                    onChange={(date) => {
                                                        if (expirationTimeField?.value)
                                                            validationTillDateField?.onChange(date);
                                                        return;
                                                    }}
                                                    dateFormat="MMMM d, yyyy h:mmaa"
                                                    timeIntervals={15}
                                                />
                                                {errors.validationTillDate && (
                                                    <p className="text-foundryRed">
                                                        {errors.validationTillDate.message}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {bountyPayload?.status === 'Funding Failed' &&
                                            bountyPayload?.rewardToken?.totalRewardAmount -
                                            bountyPayload?.fundedAmount !==
                                            0 && (
                                                <div className="my-5">
                                                    <div className="flex gap-3 mb-1">
                                                        <Heading
                                                            type="xsmall"
                                                            className="capitalize  text-textPrimary"
                                                        >
                                                            Amount to be paid
                                                            <sup className="text-lg relative top-[1px]">*</sup>
                                                        </Heading>
                                                    </div>
                                                    <div className="flex items-center mt-2 w-[100%] disabled ">
                                                        <div className="relative ">
                                                            <Dropdown
                                                                disabled={true}
                                                                modifyLabel={(listItem) => {
                                                                    return (
                                                                        <span className="flex items-center gap-5">
                                                                            <div className="relative">
                                                                                <img
                                                                                    className="w-5 h-5 rounded-full "
                                                                                    src={listItem?.image}
                                                                                    alt=""
                                                                                />
                                                                                <img
                                                                                    className="w-2 h-2 absolute top-0 right-0 rounded-full "
                                                                                    src={listItem?.chainImage}
                                                                                    alt=""
                                                                                />
                                                                            </div>

                                                                            <span>{listItem?.symbol} </span>
                                                                        </span>
                                                                    );
                                                                }}
                                                                data={[]}
                                                                isSymbol={true}
                                                                selected={bountyPayload?.tokenInfo}
                                                                // onSelected={tokenField.onChange}
                                                                // className="py-1 bg-red-400"
                                                                noSelectedText="Choose Token"
                                                                btnClass="rounded-e-none leading-6  "
                                                                w="w-40"
                                                            />
                                                        </div>
                                                        <Input
                                                            value={
                                                                bountyPayload?.rewardToken?.totalRewardAmount -
                                                                bountyPayload?.fundedAmount
                                                            }
                                                            type="number"
                                                            disabled
                                                            placeholder={'Enter amount'}
                                                            className={'w-[100%] rounded-s-none '}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                    </>
                                )}
                                <div className="flex items-center mt-5 gap-3">
                                    {isFundingByLandscape ? (

                                        <ButtonNew
                                            type={'submit'}
                                            disabled={loading}
                                            fixedMinWidth={true}
                                            variant="blue"
                                            shape='oval'
                                            size="sm"

                                        >
                                            {loading ? <Loader /> : 'Request'}
                                        </ButtonNew>
                                    ) : (

                                        <ButtonNew
                                            type={'submit'}
                                            disabled={loading}
                                            fixedMinWidth={true}
                                            variant="blue"
                                            shape='oval'
                                            size="sm"

                                        >
                                            {loading ? <Loader /> : 'Publish'}
                                        </ButtonNew>
                                    )}

                                    <ButtonNew
                                        type='button'
                                        fixedMinWidth={true}
                                        variant="neutral"
                                        shape='oval'
                                        size="sm"
                                        onClick={close}
                                    >
                                        Cancel
                                    </ButtonNew>

                                </div>
                            </form>
                        </div>
                    )}
                </ModalBox>
            </div>
        </Overlay>,
        document.getElementById('modal-root')
    );
}

export default StartBountyModal;
